import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { PiArrowClockwiseBold, PiDownloadSimpleBold, PiFireFill, PiWarningBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AI_BUTTON, AiDisclaimer, AiError, AiPlateIcon, AiThinking, aiErrorMessage, useAiStatus } from './aiShared';

type Item = {
    id: string;
    product_id: number;
    sku: string;
    product: string;
    supplier: string | null;
    color: string;
    high_turnover: boolean;
    sold_qty: number;
    monthly_demand: number;
    free_on_hand: number;
    free_in_transit: number;
    reserved: number;
    coverage_months: number;
    base_qty: number;
    priority: 'alta' | 'media' | 'baixa';
    reason: string;
    adjusted_qty: number | null;
};

type Result = {
    summary: string;
    items: Item[];
    warnings: string[];
    params: { months: number; share_pct: number; formula: string };
    generated_at: string;
    cached: boolean;
};

const PRIORITY = {
    alta: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300',
    media: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200',
    baixa: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

function downloadCsv(result: Result) {
    const head = ['Prioridade', 'SKU', 'Produto', 'Fornecedor', 'Cor', 'Vendido no período', 'Demanda/mês', 'Livre em terra', 'Livre em trânsito', 'Reservado', 'Cobertura (meses)', 'Sugestão base', 'Ajuste da IA', 'Motivo'];
    const rows = result.items.map((i) => [i.priority, i.sku, i.product, i.supplier ?? '', i.color, i.sold_qty, fmt(i.monthly_demand), i.free_on_hand, i.free_in_transit, i.reserved, fmt(i.coverage_months), i.base_qty, i.adjusted_qty ?? '', i.reason]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `sugestao-compra-estoque-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export default function StockAiSuggestionButton() {
    const ai = useAiStatus();
    const [open, setOpen] = useState(false);
    const [months, setMonths] = useState(6);
    const [share, setShare] = useState(100);

    const gen = useMutation({
        mutationFn: async (refresh: boolean) =>
            (await api.post<{ data: Result }>('/ai/stock/suggestions', { months, share_pct: share, refresh })).data.data,
    });

    if (!ai.data?.features.stock) return null;
    const r = gen.data;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn(AI_BUTTON, 'px-3 py-2 text-sm')}
            >
                <AiPlateIcon className="size-5" /> Sugestão de compra
            </button>
            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Sugestão de compra para estoque"
                description="O que repor para pronta entrega, por SKU e cor — a conta é do Hub, a IA prioriza e explica."
                size="5xl"
                footer={
                    <>
                        {r && (
                            <Button variant="outline" onClick={() => downloadCsv(r)}>
                                <PiDownloadSimpleBold className="me-1.5 size-4" /> Baixar planilha
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Fechar
                        </Button>
                        <Button isLoading={gen.isPending} onClick={() => gen.mutate(Boolean(r))}>
                            {r ? (
                                <>
                                    <PiArrowClockwiseBold className="me-1.5 size-4" /> Gerar de novo
                                </>
                            ) : (
                                'Gerar sugestão'
                            )}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex flex-wrap items-end gap-4 rounded-xl bg-gray-50 p-3 dark:bg-gray-900/50">
                        <label className="text-xs">
                            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-200">Vendas analisadas</span>
                            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900">
                                {[3, 6, 9, 12].map((m) => (
                                    <option key={m} value={m}>
                                        Últimos {m} meses
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="min-w-[16rem] flex-1 text-xs">
                            <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-200">
                                Atender da pronta entrega: <strong>{share}%</strong> das vendas
                            </span>
                            <input type="range" min={10} max={100} step={5} value={share} onChange={(e) => setShare(Number(e.target.value))} className="w-full accent-rose-500" />
                            <span className="text-[11px] text-gray-400">O restante segue importado sob encomenda.</span>
                        </label>
                    </div>

                    {gen.isPending ? (
                        <AiThinking label="Cruzando vendas, estoque e trânsito…" />
                    ) : gen.isError ? (
                        <AiError message={aiErrorMessage(gen.error)} onRetry={() => gen.mutate(false)} />
                    ) : !r ? (
                        <p className="py-8 text-center text-sm text-gray-500">
                            Escolha o período e quanto das vendas você quer atender do estoque, depois clique em <strong>Gerar sugestão</strong>.
                        </p>
                    ) : (
                        <>
                            <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3 text-sm text-gray-800 dark:border-rose-500/30 dark:bg-rose-500/5 dark:text-gray-100">
                                {r.summary}
                                {r.warnings.length > 0 && (
                                    <ul className="mt-2 space-y-1 text-xs text-amber-800 dark:text-amber-200">
                                        {r.warnings.map((w, i) => (
                                            <li key={i} className="flex gap-1.5">
                                                <PiWarningBold className="mt-0.5 size-3.5 shrink-0" />
                                                {w}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {r.items.length > 0 && (
                                <div className="max-h-[48vh] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-left text-xs">
                                        <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-2 py-2">Prior.</th>
                                                <th className="px-2 py-2">Produto · cor</th>
                                                <th className="px-2 py-2 text-end">Vendido</th>
                                                <th className="px-2 py-2 text-end">Por mês</th>
                                                <th className="px-2 py-2 text-end">Livre</th>
                                                <th className="px-2 py-2 text-end">Cobertura</th>
                                                <th className="px-2 py-2 text-end">Comprar</th>
                                                <th className="px-2 py-2">Por quê</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {r.items.map((i) => (
                                                <tr key={i.id} className="border-t border-gray-100 align-top dark:border-gray-700">
                                                    <td className="px-2 py-2">
                                                        <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold capitalize', PRIORITY[i.priority])}>{i.priority === 'media' ? 'média' : i.priority}</span>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <Link to={`/estoque/consulta/${encodeURIComponent(i.sku)}`} className="font-semibold text-gray-900 hover:text-primary dark:text-gray-100">
                                                            {i.product}
                                                        </Link>
                                                        {i.high_turnover && <PiFireFill className="ms-1 inline size-3 text-orange-500" title="Alto giro" />}
                                                        <span className="block font-mono text-[10px] text-gray-400">{i.sku}{i.supplier ? ` · ${i.supplier}` : ''}</span>
                                                        <span className="block text-[11px] text-gray-500">{i.color}</span>
                                                    </td>
                                                    <td className="px-2 py-2 text-end tabular-nums">{i.sold_qty}</td>
                                                    <td className="px-2 py-2 text-end tabular-nums">{fmt(i.monthly_demand)}</td>
                                                    <td className="px-2 py-2 text-end tabular-nums" title={`${i.free_on_hand} em terra · ${i.free_in_transit} em trânsito · ${i.reserved} reservado`}>
                                                        {i.free_on_hand + i.free_in_transit}
                                                    </td>
                                                    <td className={cn('px-2 py-2 text-end tabular-nums', i.coverage_months < 2 && 'font-semibold text-rose-600')}>{fmt(i.coverage_months)} m</td>
                                                    <td className="px-2 py-2 text-end">
                                                        <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-gray-100">{i.adjusted_qty ?? i.base_qty}</span>
                                                        {i.adjusted_qty !== null && <span className="block text-[10px] text-gray-400 line-through">{i.base_qty}</span>}
                                                    </td>
                                                    <td className="max-w-[18rem] px-2 py-2 text-[11px] text-gray-600 dark:text-gray-300">{i.reason}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <AiDisclaimer generatedAt={r.generated_at} cached={r.cached}>
                                <span>· Base: {r.params.formula}. Quando a IA ajusta, a quantidade base aparece riscada.</span>
                            </AiDisclaimer>
                        </>
                    )}
                </div>
            </V2Modal>
        </>
    );
}
